import { TypographyVariantsOptions } from '@mui/material/styles';
import {
  BODY1_LINE_HEIGHT,
  FONT_SCALE_CAP_DENSE,
  getResponsiveFontSizeAtScale,
} from './watchdutyTheme.utils';

const fontFamily = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

// If you see body1bold or subtitle1bold in figma, it means to modify partial text inside of
// the typography element like (note this example should use a <Trans> .. component for translation).:
//    <Typography variant="subtitle1">
//      It starts with normal and
//      <Box component=span" fontWeight="fontWeightMedium"> ends bolded. </Box>
//    </Typography>

// Commented out variants should not be used in new pages, but were pre-existing in the codebase so are not :undefined
// here yet. Do not use:
// subtitle2

const TYPOGRAPHY_VARIANT_DEFAULTS = {
  paragraphIndent: 0,
  paragraphSpacing: 0,
  letterSpacing: '0px',
  lineHeight: 'normal',
  fontStyle: 'normal' as const,
};

/** Labels, tabs, metadata rows — fixed at design size. */
const denseFontSize = (rem: string): string =>
  getResponsiveFontSizeAtScale(rem, undefined, {
    maxScale: FONT_SCALE_CAP_DENSE,
  });

const scaledFontSize = (rem: string): string =>
  getResponsiveFontSizeAtScale(rem);

/**
 * Typography variants for a given text-scale factor.
 *
 * Scaling policy (ENG-6571):
 * - **Scale:** primary content (`body1`, `body2`), headings (`h1`–`h3`), chips, buttons
 * - **Fixed:** stat labels (`subtitle1` in StatTable headers), tabs (`h4`–`h6`); geo-event
 *   share/notification CTAs use component-level dense overrides
 *
 * @param _scaleFactor Retained for Storybook API compatibility; font sizes use CSS `clamp()`.
 */
export const createTypographyForScaleFactor = (
  _scaleFactor: number,
): TypographyVariantsOptions => ({
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  fontFamily,
  h1: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: scaledFontSize('2rem'),
    fontWeight: 500,
  },
  h2: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: scaledFontSize('1.5rem'),
    fontWeight: 600,
  },
  h3: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: scaledFontSize('1.25rem'),
    fontWeight: 600,
  },
  h4: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: denseFontSize('1rem'),
    fontWeight: 600,
    letterSpacing: '0.2px',
    fontVariant: 'small-caps',
    textTransform: 'lowercase',
  },
  h5: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: denseFontSize('0.875rem'),
    fontWeight: 600,
  },
  h6: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: denseFontSize('0.75rem'),
    fontWeight: 600,
  },
  body1: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: scaledFontSize('1rem'),
    fontWeight: 400,
    lineHeight: BODY1_LINE_HEIGHT,
  },
  body2: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: scaledFontSize('0.875rem'),
    fontWeight: 400,
  },
  subtitle1: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: denseFontSize('0.75rem'),
    fontWeight: 400,
  },
  subtitle2: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: denseFontSize('0.75rem'),
    fontWeight: 400,
  },
  button: {
    ...TYPOGRAPHY_VARIANT_DEFAULTS,
    fontSize: scaledFontSize('1rem'),
    fontWeight: 600,
    textTransform: 'inherit',
  },
  overline: undefined,
});
